import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import NotFound from './NotFound';
import { GameShell, PageShell } from './layout';
import { GameSkeleton } from './Skeleton';
import gameStyles from '../styles/GameStyles.module.css';
import { GAME_CONFIG } from '../games';
import { getCurrentDate } from '../util';
import { config } from '../config';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import { useGameplayAccess } from '../contexts/GameplayAccessContext';

/** Validate puzzle routes and check server eligibility before mounting a game. */
const GameRouteWrapper = ({ GameComponent }) => {
  const { date } = useParams();
  const location = useLocation();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { getPuzzleInstanceId, fetchPuzzleInstance } = usePuzzleInstances();
  const accessContext = useGameplayAccess();
  const setTarget = accessContext?.setTarget;
  const [instance, setInstance] = useState(null);
  const [instanceError, setInstanceError] = useState(null);
  const [retry, setRetry] = useState(0);
  const isAdmin = user?.publicMetadata?.role === 'admin';
  const gameConfig = Object.values(GAME_CONFIG).find((game) => game.pathName === location.pathname.split('/')[1]);
  const routeKey = location.pathname + location.search + location.hash;

  const isValidDate = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return false;
    const [year, month, day] = date.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);
    return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;
  }, [date]);
  const futureBlocked = isValidDate && date > getCurrentDate() && config.ENVIRONMENT === 'production' && !isAdmin;

  useEffect(() => {
    let cancelled = false;
    if (!authLoaded || isSignedIn || !isValidDate || futureBlocked || !gameConfig) return undefined;
    setInstanceError(null);
    const load = async () => {
      try {
        const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, date) || await fetchPuzzleInstance(gameConfig.id, date);
        if (cancelled) return;
        if (!puzzleInstanceId) {
          setInstanceError('Partita non trovata. Riprova.');
          return;
        }
        setInstance({ routeKey, puzzleInstanceId, gameSlug: gameConfig.id });
      } catch (_) {
        if (!cancelled) setInstanceError('Non è stato possibile caricare la partita. Riprova.');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authLoaded, isSignedIn, isValidDate, futureBlocked, gameConfig, date, routeKey, getPuzzleInstanceId, fetchPuzzleInstance, retry]);

  useEffect(() => {
    if (!setTarget) return undefined;
    setTarget(!isSignedIn && instance?.routeKey === routeKey ? instance : null);
    return () => setTarget(null);
  }, [setTarget, instance, routeKey, isSignedIn]);

  if (!isValidDate) return <NotFound />;
  if (futureBlocked) return <NotFound customTitle="Viaggio nel tempo non possibile" customMessage="Il viaggio nel tempo non è possibile. Torna in una data successiva per giocare a questo gioco." />;
  if (authLoaded && isSignedIn) return <GameComponent />;

  const checked = instance?.routeKey === routeKey && accessContext?.state.target === instance;
  const error = instanceError || (checked && accessContext.state.error);
  if (error) return <PageShell><p role="alert">{error}</p><button onClick={() => { setRetry((value) => value + 1); accessContext?.refresh(); }}>Riprova</button></PageShell>;
  if (!authLoaded || !checked || !accessContext.state.access) {
    const actions = [{ icon: 'fa fa-question-circle', title: 'Istruzioni', disabled: true }];
    if (gameConfig?.id !== 'cloze') {
      actions.push({ icon: 'fa fa-medkit', title: 'Aiutini', disabled: true });
    }
    if (gameConfig?.id === 'scalinata') {
      actions.push({ icon: 'fa fa-eye', title: 'Mostra tutti i tentativi', disabled: true });
    }
    return (
      <GameShell gameConfig={gameConfig} actions={actions} isLoading>
        {gameConfig?.id !== 'scalinata' && (
          <p className={gameStyles.greeting}>{gameConfig?.displayMessage?.notStarted}</p>
        )}
        <div role="status" aria-label="Caricamento partita" aria-busy="true">
          <GameSkeleton gameId={gameConfig?.id} />
        </div>
      </GameShell>
    );
  }
  if (!accessContext.state.access.can_play) return null;
  return <GameComponent />;
};

export default GameRouteWrapper;
